'use client';

import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { usePathname } from 'next/navigation';
import { ReactEventHandler, useContext } from 'react';
import * as yup from 'yup';

import Button from '@/app/components/base/ui/Button';
import FormikTextField from '@/containers/InboundStone/components/InboundFormV2/components/FormikTextField';
import { AmplitudeContext } from '@/contexts/amplitude';
import useStore from '@/store';
import { getDeviceId } from '@/utils/amplitude';
import { getTranckerInfo } from '@/utils/Analytics';
import { phoneMask } from '@/utils/masks';

import { sendOneApiWhatsAppData } from '../../containers/InboundV2/services/sendOneApiWhatsAppData';

type ExclusiveOffersFormType = {
  textAlign?: string;
  title?: string;
  description?: string;
  button: { label: string; onClick: (e?: ReactEventHandler) => void };
  className?: string;
  tpvField?: boolean;
  isCNPJ?: boolean;
  fullWidthButton?: boolean;
  sectionReference?: string;
  csrfToken: string;
};
const ExclusiveOffersForm = (props: ExclusiveOffersFormType) => {
  const {
    textAlign,
    title,
    description,
    button,
    className,
    fullWidthButton = true,
    sectionReference,
    csrfToken,
  } = props;
  const { analytics } = useContext(AmplitudeContext);
  const pathname = usePathname();
  const store = useStore();

  const formikInitialValues = {
    name: '',
    email: '',
    phone: '',
    tpv: 'TPV não informado, experimento com a Lucy',
  };

  async function handleSubmit(
    values: Record<string, string | undefined>,
    { resetForm }: FormikHelpers<Record<string, string | undefined>>,
  ) {
    const formattedPhone = String(values.phone?.replace(/\D/g, ''));
    await store.updateWithDadosData({
      user: {
        document: '',
        email: String(values.email),
        full_name: String(values.name),
        phone: formattedPhone,
      },
      organization: store.organization,
    });
    resetForm();
    const metadataResponse = {
      metadata: {
        amplitude_data: {
          device_id: getDeviceId(),
          path_origin: pathname,
        },
      },
    };
    const { gaClientId, gaTrackingId } = await getTranckerInfo();
    sendOneApiWhatsAppData(
      {
        channel: 'whatsapp',
        name: String(values.name),
        phone: formattedPhone,
        email: String(values.email),
        document: '',
        gaClientId,
        gaTrackingId,
        source: 'inbound_form',
        tpv: 'TPV não informado, experimento com a Lucy',
        metadata: JSON.stringify(metadataResponse),
      },
      csrfToken,
    );
  }
  const trackOnBlurFields = (inputType: string) => {
    analytics?.track({
      event_type: `step ${inputType} completed`,
      event_properties: {
        name: `step ${inputType} completed`,
        description: `Evento disparado quando o usuário preenche o ${inputType}. Como propriedade será retornada qual o ${inputType} digitado`,
      },
    });
  };
  const schema = yup.object({
    name: yup.string().trim().required('Este campo é obrigatório!'),
    email: yup
      .string()
      .email('Informe um e-mail válido')
      .required('Este campo é obrigatório!'),
    phone: yup
      .string()
      .matches(/\(\d{2,}\) \d{4,}-\d{4}/g, 'Informe um telefone válido')
      .required('Este campo é obrigatório!'),
  });

  return (
    <Formik<Record<string, string | undefined>>
      enableReinitialize
      initialValues={formikInitialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {props => {
        const isButtonDisabled = !props.isValid;
        return (
          <Form>
            <div className={`flex flex-col ${textAlign} max-w-[440px]`}>
              <p className="mb-8 font-display font-bold heading-5">{title}</p>
              <p>{description}</p>
            </div>
            <div className="flex flex-col gap-16 mt-32 mb-16">
              <FormikTextField
                label="Nome"
                name="name"
                onBlurCallBack={(
                  event: React.FocusEvent<HTMLInputElement, Element>,
                ) => {
                  if (event.target.value) {
                    trackOnBlurFields(event.target.name);
                  }
                }}
              />
              <FormikTextField
                type="email"
                name="email"
                label="E-mail"
                onBlurCallBack={(
                  event: React.FocusEvent<HTMLInputElement, Element>,
                ) => {
                  if (event.target.value) {
                    trackOnBlurFields(event.target.name);
                  }
                }}
              />
              <FormikTextField
                type="tel"
                name="phone"
                label="Telefone"
                maskFunction={phoneMask}
                onBlurCallBack={(
                  event: React.FocusEvent<HTMLInputElement, Element>,
                ) => {
                  if (event.target.value) {
                    trackOnBlurFields(event.target.name);
                  }
                }}
              />
            </div>

            <div className="mt-24">
              <p className="text-sm text-center">
                Ao continuar, você concorda com nosso &nbsp;
                <a
                  href="https://docs.stone.com.br/aviso-de-privacidade/"
                  target="_blank"
                >
                  <span className="text-sm text-stone-500 cursor-pointer">
                    Aviso de Privacidade
                  </span>
                </a>
                .
              </p>
            </div>
            <div className={className}>
              <Button
                data-testid="button-formik"
                type="submit"
                className={classNames(
                  fullWidthButton ? '!w-full' : '',
                  'mt-32',
                )}
                onClick={() => {
                  button.onClick();
                  analytics?.track({
                    event_type: 'Forms Button stone',
                    event_properties: {
                      name: 'Forms Button stone',
                      description: `Quando o usuário preencher os dados do formulário da ultima sessão e clicar em ${button.label}`,
                      section_reference: sectionReference,
                      cta_reference: button.label,
                    },
                  });
                }}
                disabled={isButtonDisabled}
                sectionReference={title || description || 'ExclusiveOffersForm'}
              >
                {button.label}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
export default ExclusiveOffersForm;
